import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { AuthContext } from 'authentication/AuthContext';
import { createSnack } from 'snackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import BatchDetails from './BatchDetails';
import ShipperAllocation from './ShipperAllocation';
import Review from './Review';
import { initialBatchDetailsState, initialShipperAllocationState } from './initialState';
import { useViewBatchQuery } from '../batchesEndpoints';

const initialState = {
  ...initialBatchDetailsState,
  ...initialShipperAllocationState,
};

const CreateBatchWizard = ({ onClose, copyBatchId }) => {
  const [step, setStep] = useState(1);
  const [batchState, setBatchState] = useState(initialState);
  const [confirmDialogOpened, setConfirmDialogOpened] = useState(false);

  const { affiliates = {} } = useContext(AuthContext);
  const batchOwners = affiliates.filter(R.propEq('shipper', true));
  const dispatch = useDispatch();

  const handleNext = () => {
    setStep(R.inc);
  };
  const handlePrev = () => {
    setStep(R.dec);
  };
  const handleReset = () => {
    setStep(1);
    setBatchState(initialState);
  };

  const handleClose = () => {
    // batchOwner is auto-selected, so we can't include it
    // in state comparisons, or it'll never be equal to initial state.
    const omitBatchOwner = R.omit(['batchOwner']);

    if (R.equals(omitBatchOwner(batchState), omitBatchOwner(initialState))) {
      onClose();
    } else {
      setConfirmDialogOpened(true);
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpened(false);
  };

  const handleComplete = () => {
    onClose();
  };

  const {
    data: copyBatchData = {},
    isSuccess,
    isError,
  } = useViewBatchQuery({
    params: { batchId: copyBatchId },
    skip: R.isNil(copyBatchId),
  });

  !R.isNil(copyBatchId) &&
    isError &&
    dispatch(
      createSnack({
        message: `The details of batch ${copyBatchId} could not be copied.`,
        options: { variant: 'error' },
      })
    );

  const copyBatchDataProp = R.prop(R.__, copyBatchData);

  // Set default values for batchState if copyBatchData is available
  useEffect(() => {
    if (!R.prop('product', batchState) && isSuccess) {
      const batchOwner = R.find(
        R.propEq('customerIdName', R.path(['batchOwner', 'batchOwnerIdn'], copyBatchData)),
        batchOwners
      );
      const copyData = {
        component: copyBatchDataProp('component')
          ? {
              data: copyBatchDataProp('component'),
              display: copyBatchDataProp('component'),
            }
          : { data: 'N/A', display: 'N/A' },
        fuelType:
          copyBatchDataProp('fuelType') &&
          copyBatchDataProp('fuelType') !== 'Missing Product Designation'
            ? {
                data: copyBatchDataProp('fuelType'),
                display: copyBatchDataProp('fuelType'),
              }
            : null,
        origin: {
          originId: copyBatchDataProp('originId'),
          originName: copyBatchDataProp('originName'),
        },
        oxygenate: copyBatchDataProp('oxygenate') ? copyBatchDataProp('oxygenate') : 'N/A',
        product: {
          isGasoline: copyBatchDataProp('isGasoline'),
          productId: copyBatchDataProp('productId'),
          productName: copyBatchDataProp('productName'),
        },
        rvp: copyBatchDataProp('rvp')
          ? {
              data: copyBatchDataProp('rvp'),
              display: copyBatchDataProp('rvp'),
            }
          : { data: 'N/A', display: 'N/A' },
        sulphurContent: copyBatchDataProp('sulphurContent')
          ? {
              data: copyBatchDataProp('sulphurContent'),
              display: copyBatchDataProp('sulphurContent'),
            }
          : null,
      };
      if (!R.isNil(batchOwner)) {
        copyData.batchOwner = batchOwner;
      }
      setBatchState({
        ...batchState,
        ...copyData,
      });
    }
  }, [batchOwners, batchState, copyBatchData, copyBatchDataProp, isSuccess]);

  const [shipmentsValidationState, setShipmentValidationState] = useState(() =>
    R.repeat({ comment: '', volume: '' }, batchState.shipments.length)
  );

  const handleCopyBatch = () => {
    batchState.availableDate = null;
    batchState.comments = '';
    batchState.po = '';
    batchState.isFocus = true;
    setStep(1);
  };

  const stepProps = {
    batchState,
    onClose: handleClose,
    onComplete: handleComplete,
    onCopy: handleCopyBatch,
    onNext: handleNext,
    onPrev: handlePrev,
    onReset: handleReset,
    setBatchState,
    setValidationState: setShipmentValidationState,
    validationState: shipmentsValidationState,
  };

  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      open
      slotProps={{
        paper: { elevation: 0 },
      }}
    >
      {step === 1 && <BatchDetails {...stepProps} />}
      {step === 2 && <ShipperAllocation {...stepProps} />}
      {step === 3 && <Review {...stepProps} />}
      {confirmDialogOpened && (
        <Dialog
          maxWidth="xs"
          onClose={handleCloseConfirmDialog}
          open
          slotProps={{
            paper: { elevation: 10 },
          }}
          sx={{ '& .MuiDialog-paper': { height: 180, width: 330 } }}
        >
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            Are you sure you want to leave this page? You have unsaved changes.
          </DialogContent>
          <DialogActions sx={{ pb: 2, px: 2 }}>
            <Button autoFocus onClick={onClose} variant="outlined">
              Continue
            </Button>
            <Button onClick={handleCloseConfirmDialog}>Keep Editing</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

CreateBatchWizard.propTypes = {
  copyBatchId: PropTypes.string,
  onClose: PropTypes.func.isRequired, // Add validation for copyState
};

CreateBatchWizard.defaultProps = {
  copyBatchId: null, // Default value for copyState
};

export default CreateBatchWizard;
